import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Input,
  Button,
  Modal,
  Upload,
  Form,
  Select,
  Space,
  notification,
  Tag,
  Spin,
  Pagination,
  InputNumber,
  Switch,
  Divider,
  Progress,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  UploadOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import BackgroundTop from '../../components/BackgroundTop/BackgroundTop';
import DebouncedButton from '../../components/Button/Button';
import {
  getAllTests,
  updateTest,
  bulkUpdateTests,
  parseCSVForBulkUpdate,
} from '../../API/services/screeningService';
import { t } from '../../i18n/i18n';
import { AnswerTypeEnum, DifficultyTag } from '../../constants/enums';
import {
  bloomTaxonomyOptions,
  questionTypeOptions,
  durationOptions,
} from '../../constants/questions';
import './BulkUpdateTests.scss';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const BulkUpdateTests = () => {
  const dispatch = useDispatch();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ testIds: [], grouperKey: '', name: '' });
  const [selectedTest, setSelectedTest] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [csvUploadModalVisible, setCsvUploadModalVisible] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [savingTest, setSavingTest] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);

  const fetchTests = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const { testIds, ...otherFilters } = filters;
        const filterParams = {
          ...otherFilters,
          ...params,
          limit: pagination.pageSize,
          offset: (pagination.current - 1) * pagination.pageSize,
        };

        // Convert testIds array to comma-separated string for backend
        if (testIds && testIds.length > 0) {
          filterParams.testIds = testIds.join(',');
        }

        const response = await getAllTests(filterParams);

        setTests(response.collection || []);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total || 0,
        }));
      } catch (error) {
        notification.error({
          message: t('Error'),
          description: t('Failed to load tests'),
        });
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.current, pagination.pageSize]
  );

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const debouncedSearch = useCallback(
    debounce((field, value) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 500),
    []
  );

  const handleSearch = (field, value) => {
    debouncedSearch(field, value);
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
  };

  const handleViewDetail = async (test) => {
    setSelectedTest(test);

    // Process options with weights from acceptedOptions
    let processedOptions = test.options || [];
    if (test.acceptedOptions && processedOptions.length > 0) {
      processedOptions = processedOptions.map((option) => {
        const acceptedOption = test.acceptedOptions.find((ao) =>
          ao.options?.includes(option._id || option.id)
        );

        // For RADIO: each option has its own weight
        // For CHECK with hasGlobalScore: mark as correct if found in acceptedOptions
        if (test.answerType === AnswerTypeEnum.RADIO) {
          return {
            ...option,
            weight: acceptedOption?.weight || 0,
          };
        } else if (test.answerType === AnswerTypeEnum.CHECK && test.hasGlobalScore) {
          return {
            ...option,
            correct: !!acceptedOption && acceptedOption.weight > 0,
          };
        } else if (test.answerType === AnswerTypeEnum.CHECK && !test.hasGlobalScore) {
          return {
            ...option,
            weight: acceptedOption?.weight || 0,
          };
        }
        return option;
      });
    }

    // Extract global weight for CHECK with hasGlobalScore
    let globalWeight = test.maxScore || 0;
    if (
      test.answerType === AnswerTypeEnum.CHECK &&
      test.hasGlobalScore &&
      test.acceptedOptions?.length > 0
    ) {
      // Get the weight from the first acceptedOption that has weight > 0
      const correctOption = test.acceptedOptions.find((ao) => ao.weight > 0);
      if (correctOption) {
        globalWeight = correctOption.weight;
      }
    }

    // Prepare form values
    const formValues = {
      ...test,
      text: test.text || '',
      name: test.name || '',
      level: test.level || '',
      type: test.type || '',
      answerType: test.answerType || '',
      difficultyId: test.testDifficulty?.id || test.testDifficulty?._id || test.difficultyId || '',
      maxScore: test.maxScore || 0,
      weight: globalWeight,
      duration: test.duration || undefined,
      questionType: test.questionType || '',
      bloomTaxonomy: test.bloomTaxonomy || '',
      explanation: test.explanation || '',
      subSkillIds: test.subSkillIds || [],
      hasGlobalScore: test.hasGlobalScore || false,

      // Answer type specific fields
      existingCode: test.existingCode || '',
      langCode: test.langCode || '',
      langVersion: test.langVersion || '',
      expectedResult: test.expectedResult || [],
      writeResponse: test.writeResponse || '',
      isTyping: test.isTyping || false,
      maxChar: test.maxChar || undefined,
      concepts: test.concepts || '',
      promptSubType: test.promptSubType || '',
      maxAttempts: test.maxAttempts || '',
      totalTime: test.totalTime || '',
      preparationTime: test.preparationTime || '',
      options: processedOptions,
    };

    editForm.setFieldsValue(formValues);
    setDetailModalVisible(true);
  };

  const handleSaveTest = async () => {
    try {
      setSavingTest(true);
      const values = editForm.getFieldsValue();
      await updateTest(selectedTest._id, values);

      notification.success({
        message: t('Success'),
        description: t('Test updated successfully'),
      });

      setDetailModalVisible(false);
      fetchTests();
    } catch (error) {
      notification.error({
        message: t('Error'),
        description: t('Failed to update test'),
      });
    } finally {
      setSavingTest(false);
    }
  };

  const handleCSVUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const parsedData = parseCSVForBulkUpdate(csvContent);
        setCsvData(parsedData);
        setCsvUploadModalVisible(false);
        setConfirmModalVisible(true);
      } catch (error) {
        notification.error({
          message: t('Error'),
          description: error.message || t('Invalid CSV format'),
        });
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handleBulkUpdate = async () => {
    try {
      setUploadingBulk(true);
      const result = await bulkUpdateTests(csvData);

      notification.success({
        message: t('Success'),
        description: t(`${result.updated} tests updated successfully`),
      });

      setConfirmModalVisible(false);
      setCsvData(null);
      fetchTests();
    } catch (error) {
      notification.error({
        message: t('Error'),
        description: t('Failed to bulk update tests'),
      });
    } finally {
      setUploadingBulk(false);
    }
  };

  const columns = [
    {
      title: t('Test ID'),
      dataIndex: '_id',
      key: '_id',
      width: 200,
      render: (id) => <Tag>{id}</Tag>,
    },
    {
      title: t('Answer Type'),
      dataIndex: 'answerType',
      key: 'answerType',
      width: 120,
      render: (type) => <Tag color="green">{type || '-'}</Tag>,
    },
    {
      title: t('Difficulty'),
      dataIndex: 'testDifficulty',
      key: 'difficulty',
      ellipsis: true,
      render: (testDifficulty) => (
        <span title={t(testDifficulty?.name)}>{t(testDifficulty?.name) || '-'}</span>
      ),
    },
    {
      title: t('Files'),
      key: 'files',
      width: 70,
      render: (_, record) => {
        const testFiles = record.files || [];
        const optionFiles =
          record.options?.reduce((acc, opt) => {
            if (opt.files && opt.files.length > 0) {
              return acc + opt.files.length;
            }
            return acc;
          }, 0) || 0;

        if (testFiles.length === 0 && optionFiles === 0) {
          return <span>-</span>;
        }

        const fileExtensions = testFiles.reduce((acc, file) => {
          const ext = file.extension?.toLowerCase() || file.fileType?.toLowerCase() || 'unknown';
          acc[ext] = (acc[ext] || 0) + 1;
          return acc;
        }, {});

        const extensionSummary = Object.entries(fileExtensions)
          .map(([ext, count]) => `${count} .${ext}`)
          .join(', ');

        return (
          <div>
            {testFiles.length > 0 && (
              <div>
                <Tag color="blue" style={{ marginBottom: 4 }}>
                  {testFiles.length} {testFiles.length === 1 ? 'file' : 'files'}
                </Tag>
                {extensionSummary && (
                  <div style={{ fontSize: 12, color: '#666' }}>{extensionSummary}</div>
                )}
              </div>
            )}
            {optionFiles > 0 && (
              <Tag color="green" style={{ marginTop: 4 }}>
                {optionFiles} in options
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t('Theme'),
      dataIndex: 'theme',
      key: 'theme',
      ellipsis: true,
      render: (theme) => <span title={theme}>{theme || '-'}</span>,
    },
    {
      title: t('Max Score'),
      dataIndex: 'maxScore',
      key: 'maxScore',
      width: 80,
      render: (maxScore) => <Tag color="blue">{maxScore || '-'}</Tag>,
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: t('Text'),
      dataIndex: 'text',
      key: 'text',
      ellipsis: true,
      render: (text) => <span title={text}>{text || '-'}</span>,
    },
    {
      title: t('Correctness %'),
      dataIndex: 'metrics',
      key: 'correctnessPercentage',
      width: 90,
      render: (metrics) => {
        if (!metrics || !metrics.totalAttempts) return '-';
        const percentage = ((metrics.totalAttemptsCorrect / metrics.totalAttempts) * 100).toFixed(
          1
        );
        return <Tag color={percentage >= 50 ? 'green' : 'red'}>{percentage}%</Tag>;
      },
    },
    {
      title: t('Correct Attempts'),
      dataIndex: 'metrics',
      key: 'correctAttempts',
      width: 90,
      render: (metrics) => {
        if (!metrics) return '-';
        return (
          <Tag color="blue">
            {metrics.totalAttemptsCorrect || 0} / {metrics.totalAttempts || 0}
          </Tag>
        );
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => handleViewDetail(record)} size="small" />
        </Space>
      ),
    },
  ];

  return (
    <div className="bulk-update-tests">
      <BackgroundTop title={t('Bulk Update Tests')} />

      <div className="bulk-update-tests__container">
        <div className="bulk-update-tests__filters">
          <Space size="large" wrap>
            <Select
              mode="tags"
              placeholder={t('Search by Test IDs (multiple)')}
              onChange={(values) => {
                setFilters((prev) => ({ ...prev, testIds: values }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
              style={{ width: 300 }}
              allowClear
              tokenSeparators={[',']}
            />
            <Search
              placeholder={t('Search by Grouper Key')}
              onSearch={(value) => handleSearch('grouperKey', value)}
              onChange={(e) => handleSearch('grouperKey', e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Search
              placeholder={t('Search by Text')}
              onSearch={(value) => handleSearch('name', value)}
              onChange={(e) => handleSearch('name', e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setCsvUploadModalVisible(true)}
            >
              {t('Import CSV')}
            </Button>
          </Space>
        </div>

        <div className="bulk-update-tests__table">
          <Table
            columns={columns}
            dataSource={tests}
            rowKey="_id"
            loading={loading}
            pagination={false}
            scroll={{ x: 1200 }}
          />
          <div className="bulk-update-tests__pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger
              showTotal={(total) => t(`Total ${total} tests`)}
            />
          </div>
        </div>
      </div>

      <Modal
        title={t('Test Details')}
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={900}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        footer={[
          <Button key="cancel" onClick={() => setDetailModalVisible(false)}>
            {t('Cancel')}
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveTest} loading={savingTest}>
            {t('Save Changes')}
          </Button>,
        ]}
      >
        {selectedTest && (
          <Form form={editForm} layout="vertical">
            <Form.Item label={t('Test ID')}>
              <Input value={selectedTest._id} disabled />
            </Form.Item>

            {/* Metrics Information */}
            {selectedTest?.metrics && (
              <>
                <Divider>{t('Test Metrics')}</Divider>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={6}>
                    <Statistic
                      title={t('Total Attempts')}
                      value={selectedTest.metrics.totalAttempts || 0}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={t('Correct Attempts')}
                      value={selectedTest.metrics.totalAttemptsCorrect || 0}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={t('Average Time (seconds)')}
                      value={
                        selectedTest.metrics.averageTimeSpent
                          ? selectedTest.metrics.averageTimeSpent.toFixed(1)
                          : 0
                      }
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={t('Correctness Rate')}
                      value={
                        selectedTest.metrics.totalAttempts > 0
                          ? (
                              (selectedTest.metrics.totalAttemptsCorrect /
                                selectedTest.metrics.totalAttempts) *
                              100
                            ).toFixed(1)
                          : 0
                      }
                      suffix="%"
                      valueStyle={{
                        color:
                          selectedTest.metrics.totalAttempts > 0 &&
                          selectedTest.metrics.totalAttemptsCorrect /
                            selectedTest.metrics.totalAttempts >=
                            0.5
                            ? '#3f8600'
                            : '#cf1322',
                      }}
                    />
                  </Col>
                </Row>

                {/* Progress bar for correctness */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span>{t('Correctness Progress')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Progress
                      percent={
                        selectedTest.metrics.totalAttempts > 0
                          ? (selectedTest.metrics.totalAttemptsCorrect /
                              selectedTest.metrics.totalAttempts) *
                            100
                          : 0
                      }
                      status={
                        selectedTest.metrics.totalAttempts > 0 &&
                        selectedTest.metrics.totalAttemptsCorrect /
                          selectedTest.metrics.totalAttempts >=
                          0.5
                          ? 'success'
                          : 'exception'
                      }
                      showInfo={false}
                      style={{ flex: 1 }}
                    />
                    <span style={{ 
                      minWidth: 50, 
                      fontWeight: 'bold',
                      color: selectedTest.metrics.totalAttempts > 0 &&
                        selectedTest.metrics.totalAttemptsCorrect /
                          selectedTest.metrics.totalAttempts >= 0.5
                          ? '#3f8600'
                          : '#cf1322'
                    }}>
                      {selectedTest.metrics.totalAttempts > 0
                        ? ((selectedTest.metrics.totalAttemptsCorrect /
                            selectedTest.metrics.totalAttempts) *
                          100).toFixed(1)
                        : 0}%
                    </span>
                  </div>
                </div>

                {/* Radio/Check stats if available */}
                {selectedTest.metrics.radioCheckStats &&
                  selectedTest.metrics.radioCheckStats.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <h4>{t('Option Selection Statistics')}</h4>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {selectedTest.metrics.radioCheckStats.map((stat, index) => {
                          const option = selectedTest.options?.find(
                            (opt) => (opt._id || opt.id) === stat.id
                          );
                          const percentage =
                            selectedTest.metrics.totalAttempts > 0
                              ? (
                                  (stat.totalSelected / selectedTest.metrics.totalAttempts) *
                                  100
                                ).toFixed(1)
                              : 0;
                          return (
                            <div
                              key={stat.id}
                              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                            >
                              <span style={{ minWidth: 200 }}>
                                {option?.text || `Option ${index + 1}`}:
                              </span>
                              <Progress
                                percent={parseFloat(percentage)}
                                size="small"
                                style={{ flex: 1 }}
                                showInfo={false}
                              />
                              <span style={{ 
                                minWidth: 100, 
                                fontSize: 12,
                                fontWeight: 'bold'
                              }}>
                                {stat.totalSelected} ({percentage}%)
                              </span>
                            </div>
                          );
                        })}
                      </Space>
                    </div>
                  )}
              </>
            )}

            {/* Basic Information */}
            <Divider>{t('Basic Information')}</Divider>

            <Form.Item name="name" label={t('Name')}>
              <Input />
            </Form.Item>

            <Form.Item name="text" label={t('Text')}>
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item name="explanation" label={t('Explanation')}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="answerType" label={t('Answer Type')}>
              <Select
                onChange={() =>
                  editForm.resetFields([
                    'existingCode',
                    'langCode',
                    'langVersion',
                    'expectedResult',
                    'writeResponse',
                    'isTyping',
                    'maxChar',
                    'concepts',
                    'promptSubType',
                    'maxAttempts',
                    'totalTime',
                    'preparationTime',
                    'options',
                  ])
                }
              >
                {Object.entries(AnswerTypeEnum).map(([key, value]) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="difficultyId"
              label={t('Difficulty')}
              extra={
                t('Current difficulty: ') + (selectedTest?.testDifficulty?.name || t('Not set'))
              }
            >
              <Input placeholder={t('Enter difficulty ID (e.g., 63be226b32009a67f801a26a)')} />
            </Form.Item>

            <Form.Item name="maxScore" label={t('Max Score')}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="duration" label={t('Duration (minutes)')}>
              <Select>
                {durationOptions.map((opt) => (
                  <Option key={opt.id} value={opt.duration}>
                    {opt.name} {t('minutes')}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="questionType" label={t('Question Type')}>
              <Select>
                {questionTypeOptions.map((opt) => (
                  <Option key={opt.id} value={opt.questionType}>
                    {t(opt.name)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="bloomTaxonomy" label={t('Bloom Taxonomy')}>
              <Select>
                {bloomTaxonomyOptions.map((opt) => (
                  <Option key={opt.id} value={opt.bloomTaxonomy}>
                    {t(opt.name)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="subSkillIds" label={t('Subskills')}>
              <Select mode="tags" placeholder={t('Add subskill IDs')}></Select>
            </Form.Item>

            {/* Conditional fields based on answerType */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.answerType !== currentValues.answerType
              }
            >
              {({ getFieldValue }) => {
                const answerType = getFieldValue('answerType');

                return (
                  <>
                    {/* CODE fields */}
                    {answerType === AnswerTypeEnum.CODE && (
                      <>
                        <Divider>{t('Code Configuration')}</Divider>
                        <Form.Item name="existingCode" label={t('Existing Code')}>
                          <TextArea rows={6} style={{ fontFamily: 'monospace' }} />
                        </Form.Item>
                        <Form.Item name="langCode" label={t('Language Code')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="langVersion" label={t('Language Version')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="expectedResult" label={t('Expected Results')}>
                          <Form.List name="expectedResult">
                            {(fields, { add, remove }) => (
                              <>
                                {fields.map(({ key, name, ...restField }) => (
                                  <Space
                                    key={key}
                                    style={{ display: 'flex', marginBottom: 8 }}
                                    align="baseline"
                                  >
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'input']}
                                      rules={[{ required: true, message: t('Missing input') }]}
                                    >
                                      <Input placeholder={t('Input')} />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'output']}
                                      rules={[{ required: true, message: t('Missing output') }]}
                                    >
                                      <Input placeholder={t('Output')} />
                                    </Form.Item>
                                    <MinusCircleOutlined onClick={() => remove(name)} />
                                  </Space>
                                ))}
                                <Form.Item>
                                  <Button
                                    type="dashed"
                                    onClick={() => add()}
                                    block
                                    icon={<PlusOutlined />}
                                  >
                                    {t('Add Expected Result')}
                                  </Button>
                                </Form.Item>
                              </>
                            )}
                          </Form.List>
                        </Form.Item>
                      </>
                    )}

                    {/* CODE_AI fields */}
                    {answerType === AnswerTypeEnum.CODE_AI && (
                      <>
                        <Divider>{t('Code AI Configuration')}</Divider>
                        <Form.Item name="existingCode" label={t('Existing Code')}>
                          <TextArea rows={6} style={{ fontFamily: 'monospace' }} />
                        </Form.Item>
                      </>
                    )}

                    {/* RADIO/CHECK fields */}
                    {(answerType === AnswerTypeEnum.RADIO ||
                      answerType === AnswerTypeEnum.CHECK) && (
                      <>
                        <Divider>{t('Options Configuration')}</Divider>

                        {answerType === AnswerTypeEnum.CHECK && (
                          <Form.Item
                            name="hasGlobalScore"
                            valuePropName="checked"
                            label={t('Has Global Score')}
                          >
                            <Switch />
                          </Form.Item>
                        )}

                        <Form.Item name="options" label={t('Options')}>
                          <Form.List name="options">
                            {(fields, { add, remove }) => (
                              <>
                                {fields.map(({ key, name, ...restField }, index) => {
                                  const currentAnswerType = editForm.getFieldValue('answerType');
                                  const hasGlobalScore = editForm.getFieldValue('hasGlobalScore');
                                  const option = editForm.getFieldValue(['options', index]);

                                  // Find the weight from acceptedOptions
                                  let currentWeight = 0;
                                  if (selectedTest?.acceptedOptions) {
                                    const acceptedOption = selectedTest.acceptedOptions.find((ao) =>
                                      ao.options?.includes(option?._id || option?.id)
                                    );
                                    if (acceptedOption) {
                                      currentWeight = acceptedOption.weight || 0;
                                    }
                                  }

                                  return (
                                    <Space
                                      key={key}
                                      style={{
                                        display: 'flex',
                                        marginBottom: 8,
                                        alignItems: 'start',
                                        flexWrap: 'wrap',
                                      }}
                                      align="baseline"
                                    >
                                      <Form.Item {...restField} name={[name, 'id']} hidden>
                                        <Input />
                                      </Form.Item>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'text']}
                                        rules={[{ required: true, message: t('Missing text') }]}
                                        style={{ width: 250 }}
                                      >
                                        <Input placeholder={t('Option text')} />
                                      </Form.Item>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'formula']}
                                        style={{ width: 180 }}
                                      >
                                        <Input placeholder={t('Formula (optional)')} />
                                      </Form.Item>

                                      {/* For RADIO: always show weight. For CHECK: show weight only if NOT hasGlobalScore */}
                                      {(currentAnswerType === AnswerTypeEnum.RADIO ||
                                        !hasGlobalScore) && (
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'weight']}
                                          label={t('Weight')}
                                          initialValue={currentWeight}
                                          style={{ marginBottom: 0 }}
                                        >
                                          <InputNumber min={0} max={10} />
                                        </Form.Item>
                                      )}

                                      {/* For CHECK with global score: show correct checkbox */}
                                      {currentAnswerType === AnswerTypeEnum.CHECK &&
                                        hasGlobalScore && (
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'correct']}
                                            valuePropName="checked"
                                            style={{ marginBottom: 0 }}
                                          >
                                            <Switch
                                              checkedChildren={t('Correct')}
                                              unCheckedChildren={t('Incorrect')}
                                            />
                                          </Form.Item>
                                        )}

                                      <Form.Item
                                        {...restField}
                                        name={[name, 'pinnedIndex']}
                                        style={{ width: 100 }}
                                      >
                                        <InputNumber placeholder={t('Pin Index')} min={0} />
                                      </Form.Item>

                                      <MinusCircleOutlined onClick={() => remove(name)} />
                                    </Space>
                                  );
                                })}
                                <Form.Item>
                                  <Button
                                    type="dashed"
                                    onClick={() => add()}
                                    block
                                    icon={<PlusOutlined />}
                                  >
                                    {t('Add Option')}
                                  </Button>
                                </Form.Item>
                              </>
                            )}
                          </Form.List>
                        </Form.Item>

                        {/* Global weight for CHECK type with hasGlobalScore */}
                        {answerType === AnswerTypeEnum.CHECK && (
                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                              prevValues.hasGlobalScore !== currentValues.hasGlobalScore
                            }
                          >
                            {({ getFieldValue }) => {
                              const hasGlobalScore = getFieldValue('hasGlobalScore');
                              return hasGlobalScore ? (
                                <Form.Item
                                  name="weight"
                                  label={t('Global Weight for Correct Options')}
                                >
                                  <InputNumber min={0} max={10} style={{ width: '100%' }} />
                                </Form.Item>
                              ) : null;
                            }}
                          </Form.Item>
                        )}
                      </>
                    )}

                    {/* WRITE fields */}
                    {answerType === AnswerTypeEnum.WRITE && (
                      <>
                        <Divider>{t('Write Configuration')}</Divider>
                        <Form.Item name="writeResponse" label={t('Write Response')}>
                          <TextArea rows={4} />
                        </Form.Item>
                        <Form.Item name="isTyping" label={t('Is Typing')} valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </>
                    )}

                    {/* TEXT fields */}
                    {answerType === AnswerTypeEnum.TEXT && (
                      <>
                        <Divider>{t('Text Configuration')}</Divider>
                        <Form.Item name="maxChar" label={t('Max Characters')}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </>
                    )}

                    {/* TEXT_AI fields */}
                    {answerType === AnswerTypeEnum.TEXT_AI && (
                      <>
                        <Divider>{t('Text AI Configuration')}</Divider>
                        <Form.Item name="maxChar" label={t('Max Characters')}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="concepts" label={t('Concepts')}>
                          <TextArea rows={3} />
                        </Form.Item>
                        <Form.Item name="promptSubType" label={t('Prompt Sub Type')}>
                          <Input />
                        </Form.Item>
                      </>
                    )}

                    {/* VIDEO/DRAW fields */}
                    {(answerType === AnswerTypeEnum.VIDEO ||
                      answerType === AnswerTypeEnum.DRAW) && (
                      <>
                        <Divider>{t('Video/Draw Configuration')}</Divider>
                        <Form.Item name="maxAttempts" label={t('Max Attempts')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="totalTime" label={t('Total Time')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="preparationTime" label={t('Preparation Time')}>
                          <Input />
                        </Form.Item>
                      </>
                    )}
                  </>
                );
              }}
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={t('Import CSV')}
        visible={csvUploadModalVisible}
        onCancel={() => setCsvUploadModalVisible(false)}
        footer={null}
      >
        <div className="csv-upload-modal">
          <p>{t('Upload a CSV file with the following format:')}</p>
          <code>testId,property1,property2,...</code>
          <br />
          <code>test123,newValue1,newValue2,...</code>
          <br />
          <br />
          <Upload accept=".csv" beforeUpload={handleCSVUpload} showUploadList={false}>
            <Button icon={<UploadOutlined />}>{t('Select CSV File')}</Button>
          </Upload>
        </div>
      </Modal>

      <Modal
        title={t('Confirm Bulk Update')}
        visible={confirmModalVisible}
        onCancel={() => {
          setConfirmModalVisible(false);
          setCsvData(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setConfirmModalVisible(false);
              setCsvData(null);
            }}
          >
            {t('Cancel')}
          </Button>,
          <Button key="confirm" type="primary" onClick={handleBulkUpdate} loading={uploadingBulk}>
            {t('Confirm Update')}
          </Button>,
        ]}
      >
        {csvData && (
          <div>
            <p>{t(`You are about to update ${csvData.tests.length} tests.`)}</p>
            <p>{t('Please review and confirm.')}</p>
            <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 16 }}>
              <Table
                size="small"
                dataSource={csvData.tests.slice(0, 10)}
                columns={[
                  { title: 'Test ID', dataIndex: 'testId', key: 'testId' },
                  ...Object.keys(csvData.tests[0]?.data || {}).map((key) => ({
                    title: key,
                    dataIndex: ['data', key],
                    key,
                  })),
                ]}
                pagination={false}
              />
              {csvData.tests.length > 10 && (
                <p style={{ marginTop: 8 }}>
                  {t(`... and ${csvData.tests.length - 10} more tests`)}
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BulkUpdateTests;
